import { StaticModal, createModal } from '@nexelem/modals';
import { Button } from '@nexelem/components';
import { FilterData } from '@nexelem/filters';
import { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { useStore } from 'zustand';
import { useCustomTranslation } from '../../../../hooks';
import { openCreateOrEditFilterModal } from '../CreateOrEditFilterModal';
import { GridFiltersProps } from '@nexelem/table';

type OverrideFilterModalProps = Pick<GridFiltersProps, 'store'> & {
  readonly id: string;
  readonly name: string;
  readonly filters?: FilterData;
  readonly onApplyFilters: (filters: FilterData) => void;
};

const OverrideFilterModal = ({ id, name, store, filters, onApplyFilters }: OverrideFilterModalProps) => {
  const { tWrapper } = useCustomTranslation('components.filters.overrideModal');
  const addGridSettings = useStore(store, (state) => state.addSettings);
  const columns = useStore(store, (state) => state.currentColumnsSettings);
  const selectedFilter = useStore(store, (state) => state.settings.find((_) => _.id === id));

  const handleOverrideFilter = useCallback(() => {
    if (selectedFilter && filters) {
      addGridSettings({ ...selectedFilter, columns, filters, id });
      onApplyFilters(filters);
      closeOverrideFilterModal();
    }
  }, [addGridSettings, columns, filters, id, onApplyFilters, selectedFilter]);

  return (
    <StaticModal maxWidth="lg" onClose={closeOverrideFilterModal}>
      <StaticModal.Header withCloseButton>{tWrapper('header')}</StaticModal.Header>
      <StaticModal.Content className="flex flex-col gap-6">
        <span className="text-s18 text-text-primary">
          <Trans components={{ strong: <strong /> }}>{tWrapper('subtitle', { name })}</Trans>
        </span>
      </StaticModal.Content>
      <StaticModal.Footer>
        <Button variant="text" onClick={closeOverrideFilterModal}>
          {tWrapper('buttons.cancel')}
        </Button>
        <Button variant="outlined" onClick={() => openCreateOrEditFilterModal({ filters, store })}>
          {tWrapper('buttons.saveAsNew')}
        </Button>
        <Button variant="contained" onClick={handleOverrideFilter}>
          {tWrapper('buttons.override')}
        </Button>
      </StaticModal.Footer>
    </StaticModal>
  );
};

export const { openOverrideFilterModal, closeOverrideFilterModal } = createModal(OverrideFilterModal, {
  name: 'OverrideFilterModal',
});
